import Vue from "vue";
import Vuex from "vuex";
import { getField, updateField } from "vuex-map-fields";
import axios from "axios";
import 'regenerator-runtime/runtime';

Vue.use(Vuex);

export default new Vuex.Store({
  plugins: [],
  state: {
    config: {
      baseURL: "",
      qikBaseURL: "",
      sandstoneBaseURL: "",
    },
    inlineLinkSelected: '',
    glossaryItemSelected: '',
    termInvestmentCalculator: {
      deposit: '',
      selectedRate: '',
      selectedFrequency: '',
      selectedMaturity: '',
      ratesTerms: [],
      termList: [
        { id: 1, label: '3 months', term: 3 },
        { id: 2, label: '6 months', term: 6 },
        { id: 3, label: '9 months', term: 9 },
        { id: 4, label: '12 months', term: 12 },
        { id: 5, label: '18 months', term: 18 },
        { id: 6, label: '24 months', term: 24 },
        { id: 7, label: '36 months', term: 36 }
      ],
      frequencyOptions: [
        { id: 1, label: 'Paid quarterly', value: 'payoutqtr', summary: 'Quarterly', url: 'QUARTERLY'},
        { id: 2, label: 'Reinvested quarterly', value: 'compoundqtr', summary: 'Reinvested Quarterly', url: 'QUARTERLY'},
        { id: 3, label: 'Paid at maturity', value: 'compound', summary: 'At Maturity', url: 'AT_MATURITY'},
        { id: 4, label: 'Paid monthly', value: 'payout', summary: 'Monthly', url: 'MONTHLY'}
      ],
      maturityOptions: [
        { id: 1, label: 'Reinvest', value: 'reinvest', summary: 'Reinvest to same term', url: 'PRINCIPAL_AND_INTEREST_ROLLOVER'},
        { id: 2, label: 'Pay out', value: 'payout', summary: 'Payout', url: 'PAY_OUT_FUNDS'},
        { id: 3, label: 'Reinvest principal', value: 'riprincipal', summary: 'Reinvest principal only to same term', url: 'PRINCIPAL_ROLLOVER'}
      ]
    },
    qik_token: '',
    qik_errors: {},
    creditcardApplication: {
      submission: {
        sbs_member: '',
        firstnames: '',
        lastname: '',
        member_number: '',
        dob_day: '',
        dob_month: '',
        dob_year: '',
        sms_verification_code: '',
      },
      form_errors: {
        member_number: '',
        sms_verification_code: '',
      }
    },
    temporaryPassword: {
      submission: {
        member_number: '',
      }
    },
  },
  getters: {
    getField,
    getInvestmentRates: (state) => state.investmentRates,
  },
  mutations: {
    updateField,
    SET_INVESTMENT_RATES(state, rates) {
      // convert to proper JSON object if it's a string
      const parsedRates = typeof rates === 'string' ? JSON.parse(rates) : rates;

      // Ensure all values have exactly two decimal places
      state.termInvestmentCalculator.ratesTerms = parsedRates.map(rate => ({
        ...rate,
        value: rate.value.toFixed(2) // Keeps it as a string with two decimals
      }));
    },
    SET_CREDIT_CARD_APPLICATION_CONTENT(state, content) {
      state.creditcardApplication.content = content;
    },
    SET_QIK_BASE_URL(state, baseURL) {
      state.config.qikBaseURL = baseURL;
    },
    SET_QIK_TOKEN(state, token) {
      state.qik_token = token;
    },
    SET_MEMBER_NUMBER_ERROR(state, message) {
      state.creditcardApplication.form_errors.member_number = message;
    },
    SET_VERIFICATION_CODE_ERROR(state, message) {
      state.creditcardApplication.form_errors.sms_verification_code = message;
    },
    SET_SANDSTONE_BASE_URL(state, baseURL) {
      state.config.sandstoneBaseURL = baseURL;
    },
  },
  actions: {
    async retrieveRatesForTermInvestment({ commit }, id) {
      
      const baseURL = window.location.origin + window.location.pathname;

      const requestURL = `${baseURL}/retrieveRatesForTermInvestmentCalculator/${id}`;

      try {
        const response = await axios.get(requestURL);
        if (response.data && response.data.CurrentRates) {

          // console.log('CurrentRates:', response.data.CurrentRates);

          commit('SET_INVESTMENT_RATES', response.data.CurrentRates);

        } else {
          console.error('Invalid response format:', response.data);
        }
      } catch (error) {
        console.error('Error fetching investment rates:', error);
      }
    },
    async retrieveCreditCardApplicationContent({ commit }, elementID) {

      const baseURL = window.location.origin + window.location.pathname;

      const requestURL = `${baseURL}/retrieveCreditCardApplicationContent/${elementID}`;

      try {
        const response = await axios.get(requestURL);
        if (response.data) {
          commit('SET_CREDIT_CARD_APPLICATION_CONTENT', response.data);
        } else {
          console.error('Invalid response format:', response.data);
        }
      } catch (error) {
        console.error('Error fetching application content:', error);
      }
    },
    async generateQikToken({ commit }) {

      const baseURL = window.location.origin + window.location.pathname;
      
      // Trigger the PHP function using Axios to call the PHP endpoint
      try {             
          
        // Make the API call to the server-side function
        const response = await axios.post(`${baseURL}/generateQikRequestCode`);

        if (response.data.error) {
          commit('SET_MEMBER_NUMBER_ERROR', `Error: ${response.data.error}`);
          return null;
        }

        commit('SET_QIK_TOKEN', response.data);
        return response.data;

      } catch (error) {
        const message =
          error.response?.data?.error ??
          "Error generating verification code. Please try again.";

        commit('SET_VERIFICATION_CODE_ERROR', `Error: ${message}`);
        return null;          
      }
    },
    async retrieveQikBaseURL({ commit }) {
      const baseURL = window.location.origin + window.location.pathname;

      const requestURL = `${baseURL}/retrieveQikBaseURL`;

      try {
        const response = await axios.get(requestURL);
        if (response.data && response.data.QikBaseURL) {
          commit('SET_QIK_BASE_URL', response.data.QikBaseURL);
        } else {
          console.error('Invalid response format:', response.data);
        }
      } catch (error) {
        console.error('Error fetching Qik base URL:', error);
      }
    },
    async retrieveStandstoneBaseURL({ commit }) {

      const baseURL = window.location.origin + window.location.pathname;

      const requestURL = `${baseURL}/retrieveSandstoneBaseURL`;

      try {
        const response = await axios.get(requestURL);
        if (response.data && response.data.sandstoneBaseURL) {
          commit('SET_SANDSTONE_BASE_URL', response.data.sandstoneBaseURL);
        } else {
          console.error('Invalid response format:', response.data);
        }
      } catch (error) {
        console.error('Error fetching Sandstone base URL:', error);
      }
    }
      
  },
});
